/** @jsx isolateComponent */
import { Children } from 'react';
import { css } from '@emotion/react';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { Colors, Typography } from '../../../theme';
import { Icon } from '../../display/Icon';

type Props = {
  /**
   * The `Link`s to render within `Breadcrumbs`. They will be wrapped in
   * an `ol > li` structure for accessibility purposes.
   */
  children: React.ReactNode;
  /**
   * The size of the breadcrumbs.
   * @default 'large'
   */
  size?: 'small' | 'large';
};

const Styles = labelStyles('Breadcrumbs', {
  root: css`
    color: ${Colors.neutral800};
    &[data-size='large'] {
      ${Typography.paragraph1ShortSemibold};
    }
    &[data-size='small'] {
      ${Typography.smallPrint1Semibold};
    }
  `,
  list: css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 2px 0px;
  `,
  item: css`
    display: flex;
    align-items: inherit;
    gap: inherit;
  `,
});

const defaultProps = {
  size: 'large' as 'large',
};

/**
 * Navigation for rendering a shallow or deep hierarchy of pages
 */
export const Breadcrumbs = (_props: Props) => {
  const props = {
    ...defaultProps,
    ..._props,
  };

  const iconSize = {
    small: 20,
    large: 24,
  }[props.size];

  const items = Children.toArray(props.children)
    .filter(Boolean)
    .map((link: React.ReactNode, i, links) => {
      const icon = i !== links.length - 1 && (
        <span aria-hidden="true">
          <Icon
            css={Styles.icon}
            isBlock={true}
            color="neutral400"
            name="arrowSmallRight"
            size={iconSize}
          />
        </span>
      );
      // Using `i` for `key` since there isn't anything else great
      // to use. Since `Breadcrumbs` should have a consistent hierarchy,
      // this should be fine. Consumers can change the key on `Breadcrumbs`
      // to force it to remount if necessary.
      return (
        <li css={Styles.item} key={i}>
          {link}
          {icon}
        </li>
      );
    });
  return (
    <div css={Styles.root} data-size={props.size}>
      <ol css={Styles.list}>{items}</ol>
    </div>
  );
};
